import React from 'react';

/**
 * Parses the athlete analysis text which is formatted as numbered sentences.
 * @param {string} analysisText - The raw analysis text from the API
 * @returns {Object} An object containing parsed analysis points and prognosis
 */
export const parseAthleteAnalysis = (analysisText) => {
  if (!analysisText) {
    return { points: [], prognosis: null };
  }

  // First, check for the "**PROGNOSIS:**" format which includes asterisks
  const prognosisAsteriskMatch = analysisText.match(/\*\*PROGNOSIS:\*\*\s*(.*?)(?:\s+\d+\.|\s*$)/s);
  
  // Also check for regular "PROGNOSIS:" format
  const prognosisNormalMatch = !prognosisAsteriskMatch && analysisText.match(/PROGNOSIS:\s*(.*?)(?:\s+\d+\.|\s*$)/s);
  
  // Check for other prognosis formats
  const aiPrognosisMatch = !prognosisAsteriskMatch && !prognosisNormalMatch && 
    (analysisText.match(/AI Prognosis\s*\n\*\*(.*?)(?:\*\*|\n\s*\d+\.|\n\s*$)/s) || 
     analysisText.match(/Prognosis\s*\n\*\*(.*?)(?:\*\*|\n\s*\d+\.|\n\s*$)/s));
  
  // Also try to match just "AI Prognosis" or "Prognosis" followed by text without asterisks
  const simplePrognosisMatch = !prognosisAsteriskMatch && !prognosisNormalMatch && !aiPrognosisMatch && 
    (analysisText.match(/AI Prognosis\s*\n(.*?)(?:\n\s*\d+\.|\n\s*$)/s) || 
     analysisText.match(/Prognosis\s*\n(.*?)(?:\n\s*\d+\.|\n\s*$)/s));

  // Use the first match we found
  let prognosis = null;
  let contentText = analysisText;
  
  if (prognosisAsteriskMatch) {
    prognosis = prognosisAsteriskMatch[1].trim();
    contentText = analysisText.replace(/\*\*PROGNOSIS:\*\*\s*(.*?)(?=\s+\d+\.|\s*$)/s, '').trim();
  } else if (prognosisNormalMatch) {
    prognosis = prognosisNormalMatch[1].trim();
    contentText = analysisText.replace(/PROGNOSIS:\s*(.*?)(?=\s+\d+\.|\s*$)/s, '').trim();
  } else if (aiPrognosisMatch) {
    // Extract the prognosis without the asterisks
    prognosis = aiPrognosisMatch[1].trim().replace(/^\*\*|\*\*$/g, '');
    
    // Find the complete prognosis block (both "AI Prognosis" or "Prognosis" headers)
    const fullMatchRegExp = /(AI Prognosis|Prognosis)\s*\n\*\*(.*?)(?:\*\*|\n\s*\d+\.)/s;
    const fullMatch = analysisText.match(fullMatchRegExp);
    
    if (fullMatch) {
      // Remove the entire prognosis section including both variants if they exist
      contentText = analysisText.replace(/(?:Prognosis\s*\n\*\*.*?\*\*\s*\n+)?(?:AI Prognosis\s*\n\*\*.*?\*\*\s*\n+)?/s, '');
    }
  } else if (simplePrognosisMatch) {
    prognosis = simplePrognosisMatch[1].trim();
    
    // Find the complete prognosis block (both "AI Prognosis" or "Prognosis" headers)
    const fullMatchRegExp = /(AI Prognosis|Prognosis)\s*\n(.*?)(?:\n\s*\d+\.)/s;
    const fullMatch = analysisText.match(fullMatchRegExp);
    
    if (fullMatch) {
      // Remove the entire prognosis section including both variants if they exist
      contentText = analysisText.replace(/(?:Prognosis\s*\n.*?\n+)?(?:AI Prognosis\s*\n.*?\n+)?/s, '');
    }
  }

  // Clean the content by removing any standalone asterisks or empty numbered points
  contentText = contentText.replace(/^\s*\*\*\s*$/gm, '').trim();
  contentText = contentText.replace(/\*\*/g, '');  // Remove all remaining asterisks

  // Process each line to check for numbered sentences, handling markdown asterisks
  const lines = contentText.split('\n').filter(line => line.trim() !== '');
  const points = [];
  
  lines.forEach(line => {
    // Check for numbered lines with or without markdown formatting
    // This regex matches: 
    // - Optional asterisks and whitespace at the beginning
    // - A number followed by a period and whitespace
    // - The content
    // - Optional asterisks at the end
    const numberMatch = line.match(/^\s*(?:\*\*)?\s*(\d+)\.?\s+(.+?)(?:\*\*)?$/);
    
    if (numberMatch) {
      // Only add if there's actual content
      if (numberMatch[2].trim()) {
        points.push({
          id: numberMatch[1],
          content: numberMatch[2].trim().replace(/^\*\*|\*\*$/g, '') // Remove any asterisks from content
        });
      }
    } else {
      // Handle lines that don't start with numbers
      if (points.length > 0) {
        const lastPoint = points[points.length - 1];
        const cleanedLine = line.trim().replace(/^\*\*|\*\*$/g, '');
        if (cleanedLine) {
          lastPoint.content += ' ' + cleanedLine;
        }
      } else if (line.trim() && !line.trim().match(/^\*\*\s*\*\*$/)) {
        // If no previous points and not a markdown separator, treat as first point
        // But ignore lines that are just ** **
        points.push({
          id: '1',
          content: line.trim().replace(/^\*\*|\*\*$/g, '')
        });
      }
    }
  });

  // Handle cases where we might have empty points or points with just asterisks
  const filteredPoints = points.filter(point => {
    const content = point.content.replace(/\*\*/g, '').trim();
    return content !== '';
  });

  // If we ended up with no points but have content text, try a fallback approach
  if (filteredPoints.length === 0 && contentText.trim()) {
    // Try to find numbered sentences in the whole text
    const fallbackRegex = /(\d+)\.\s+([^1-9]+?)(?=\s*\d+\.|$)/gs;
    let match;
    
    while ((match = fallbackRegex.exec(contentText)) !== null) {
      const content = match[2].trim().replace(/^\*\*|\*\*$/g, '');
      if (content) {
        filteredPoints.push({
          id: match[1],
          content: content
        });
      }
    }

    // If still no matches, use the entire text as one point
    if (filteredPoints.length === 0) {
      const cleanedText = contentText.trim().replace(/^\*\*|\*\*$/g, '');
      if (cleanedText) {
        filteredPoints.push({
          id: '1',
          content: cleanedText
        });
      }
    }
  }

  return { points: filteredPoints, prognosis };
};

/**
 * Component to display a single analysis point with a numbered bullet
 */
export const AthleteAnalysisPoint = ({ number, content }) => {
  return (
    <div className="flex gap-4 p-5 bg-white rounded-xl shadow-sm border border-gray-100 hover:border-red-100 hover:shadow-md transition-all duration-300">
      <div className="flex-shrink-0">
        <span className="flex items-center justify-center h-8 w-8 rounded-full bg-red-50 text-red-600 font-semibold text-sm">
          {number}
        </span>
      </div>
      <div className="flex-grow">
        <p className="text-gray-700 leading-relaxed">{content}</p>
      </div>
    </div>
  );
};

/**
 * Component to display the full athlete analysis
 */
export const AthleteAnalysisDisplay = ({ analysisText }) => {
  const { points, prognosis } = parseAthleteAnalysis(analysisText);

  if (points.length === 0) {
    return (
      <div className="text-center text-gray-500 py-6">
        No analysis available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {prognosis && (
        <div className="mb-6">
          <div className="relative bg-gradient-to-r from-gray-800 to-gray-900 p-4 rounded-xl shadow-lg overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:6px_6px]"></div>
            
            {/* Glow effects */}
            <div className="absolute -top-8 -left-8 w-16 h-16 bg-red-400/20 rounded-full blur-xl"></div>
            <div className="absolute -bottom-8 -right-8 w-16 h-16 bg-blue-400/20 rounded-full blur-xl"></div>
            
            <div className="relative flex items-start gap-3">
              <div className="flex-shrink-0 p-2 bg-white/10 backdrop-blur-sm rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                  <path d="M3 3v5h5"></path>
                  <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0-6.74-2.74L21 16"></path>
                  <path d="M16 16h5v5"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white/80 mb-1">AI Prognosis</h3>
                <p className="text-white text-base font-medium leading-tight">{prognosis}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {points.map((point) => (
        <AthleteAnalysisPoint 
          key={point.id} 
          number={point.id} 
          content={point.content} 
        />
      ))}
    </div>
  );
};
